import React, { memo, useMemo } from 'react';
import {
    AdCardDetails,
    AdCardDetailsLocation,
    AdCardDetailsPrice,
    AdCardDetailsTitle,
    AdCardImage,
    AdCardWrapper
} from './OfferCard.components';
import { Card } from '@olxui/core/dist/core/Card/Card';
import Skeleton from 'react-loading-skeleton';
import emptyCircle from './assets/no_thumbnail.svg';
import { getResponsiveImageAttributes } from '~utils/getResponsiveImageAttributes';
import { ListingViewType, Platform } from '~types';
import { AdData } from '@olxpwa-lib/utils-ad/types';
import { useAppContext } from '~state/App.context';
import { useLocalisationContext } from '@olxpwa-lib/i18n/client';
import { TestId } from '~testId';
import { getBaseApiUrl } from '~utils';

type CardOfferProps = {
    ad: AdData | null;
    bookingSummary?: boolean;
};

export const renderLocation = (ad: AdData) => {
    const { cityName, districtName } = ad?.location;
    return `${cityName}${districtName ? ` • ${districtName}` : ''}`;
};

export const LoadingSkeleton = () => {
    return (
        <AdCardWrapper data-testid={TestId.adDetailsLoading}>
            <AdCardImage>
                <Skeleton width={150} height={112} />
            </AdCardImage>
            <AdCardDetails>
                <AdCardDetailsTitle>
                    <Skeleton width={200} height={20} />
                </AdCardDetailsTitle>
                <div>
                    <AdCardDetailsPrice>
                        <Skeleton width={50} height={20} />
                    </AdCardDetailsPrice>
                    <AdCardDetailsLocation>
                        <Skeleton width={170} height={20} />
                    </AdCardDetailsLocation>
                </div>
            </AdCardDetails>
        </AdCardWrapper>
    );
};

export const renderImage = (ad: AdData) => {
    const photo = ad.photos[0] || '';

    if (!photo) {
        return (
            <Card.Thumbnail
                data-testid={TestId.emptyThumbnail}
                thumbnail={emptyCircle}
                fitThumbnail={true}
                alt={ad?.title}
            />
        );
    }
    const [src, srcSet, sizes] = getResponsiveImageAttributes(
        photo,
        ListingViewType.LIST
    );
    return (
        <Card.Thumbnail
            data-testid={TestId.thumbnail}
            fallbackSrc={emptyCircle}
            thumbnail={src}
            alt={ad?.title}
            srcSet={srcSet}
            sizes={sizes}
        />
    );
};

const getAnchorAttributes = (
    bookingSummary: boolean,
    baseUrl: string,
    ad: AdData
) => {
    if (!bookingSummary) {
        return {
            href: `${baseUrl}${ad?.id}`,
            target: '_blank',
            'aria-label': 'Link to ad'
        };
    }
};

export const OfferCard = memo(
    ({ ad, bookingSummary = false }: CardOfferProps) => {
        const { appConfig } = useAppContext();
        const {
            marketMetadata: { countryCode }
        } = useLocalisationContext();
        const { env, platform } = appConfig;

        const baseUrl = useMemo(
            () => getBaseApiUrl(env, countryCode),
            [countryCode, env]
        );

        return !ad ? (
            <LoadingSkeleton />
        ) : (
            <AdCardWrapper
                as={bookingSummary ? 'div' : 'a'}
                data-testid={TestId.adDetailsWrapper}
                bookingSummary={bookingSummary}
                {...getAnchorAttributes(bookingSummary, baseUrl, ad)}
            >
                <AdCardImage bookingSummary={bookingSummary}>
                    {renderImage(ad)}
                </AdCardImage>
                <AdCardDetails bookingSummary={bookingSummary}>
                    <AdCardDetailsTitle
                        bookingSummary={bookingSummary}
                        isMobile={platform === Platform.MOBILE}
                        data-testid={TestId.adDetailsTitle}
                    >
                        {ad?.title}
                    </AdCardDetailsTitle>
                    <div>
                        <AdCardDetailsPrice
                            bookingSummary={bookingSummary}
                            data-testid={TestId.adDetailsPrice}
                        >
                            {ad?.price?.displayValue}
                        </AdCardDetailsPrice>
                        {!bookingSummary && (
                            <AdCardDetailsLocation
                                data-testid={TestId.adDetailsLocation}
                            >
                                {renderLocation(ad) as string}
                            </AdCardDetailsLocation>
                        )}
                    </div>
                </AdCardDetails>
            </AdCardWrapper>
        );
    }
);
